import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useAuthStore } from '../store/authStore';
import { LoadingWidget, AppBar, FadeInAnimation } from '../../../core/components/ui';
import toast from 'react-hot-toast';
import { User } from '../../../core/types';

const OtpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();

  const state = location.state as { phoneNumber: string; user: User; isLogin: boolean } | null;
  const phoneNumber = state?.phoneNumber || '';
  const user = state?.user;
  const isLogin = state?.isLogin ?? true;

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!phoneNumber) navigate(ROUTES.LOGIN, { replace: true });
  }, [phoneNumber, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newOtp.every(d => d !== '')) verifyCode(newOtp.join(''));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setOtp(text.split(''));
      verifyCode(text);
    }
  };

  const verifyCode = async (code: string) => {
    if (code.length < 6) return;
    setLoading(true);
    try {
      const verified = await authStore.verifyLogin({ code, phoneNumber });
      if (!verified) {
        toast.error(authStore.lastVerifyError || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        return;
      }
      if (user) authStore.cacheUser(user);
      if (isLogin) {
        const hasPrefs = !!authStore.retrieveLocalPrefs()?.id;
        navigate(hasPrefs ? ROUTES.INDEX : ROUTES.PREFERENCE, { replace: true });
      }
      
      
      else {
        navigate(ROUTES.PREFERENCE, { replace: true });
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    await authStore.sendOtp(phoneNumber);
    setResendCooldown(30);
    setOtp(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
    setLoading(false);
    toast.success('OTP resent!');
  };

  const maskedPhone = phoneNumber
    ? phoneNumber.slice(0, -4).replace(/\d/g, '*') + phoneNumber.slice(-4)
    : '';

  return (
    <div className="otp-page">
      {loading && <LoadingWidget />}
      <AppBar onBack={() => navigate(-1)} transparent style={{ width: '100%', maxWidth: 'var(--form-max-width)' }} />

      <div className="otp-page-inner">
        <header className="otp-header">
          <FadeInAnimation delay={0}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'rgba(84, 178, 187, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0E4246" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.83 7.3a19.79 19.79 0 01-3.07-8.67A2 2 0 012.74 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 9.91a16 16 0 006 6l.91-.91a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </div>
          </FadeInAnimation>
          <FadeInAnimation delay={0.2}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-primary)', margin: 0 }}>
              Enter Verification Code
            </h2>
          </FadeInAnimation>
          <FadeInAnimation delay={0.3}>
            <p style={{ fontSize: 14, color: 'var(--color-on-surface-variant)', margin: 0, lineHeight: 1.5, maxWidth: 320 }}>
              We sent a 6-digit code to{' '}
              <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{maskedPhone}</span>
            </p>
          </FadeInAnimation>
        </header>

        <div className="otp-card">
          <FadeInAnimation delay={0.4} style={{ width: '100%' }}>
            <div className="otp-digits" onPaste={handlePaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el: HTMLInputElement | null) => { inputRefs.current[i] = el; }}
                  className="otp-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  autoFocus={i === 0}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>
          </FadeInAnimation>

          <div className="otp-actions">
            <FadeInAnimation delay={0.5} style={{ width: '100%' }}>
              <button
                type="button"
                className="btn-gradient otp-verify-btn"
                onClick={() => verifyCode(otp.join(''))}
                disabled={loading || otp.some(d => !d)}
              >
                Verify
              </button>
            </FadeInAnimation>

            <FadeInAnimation delay={0.6} style={{ width: '100%' }}>
              <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--color-on-surface-variant)' }}>
                Didn&apos;t receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  style={{
                    background: 'none',
                    color: resendCooldown > 0 ? 'var(--color-on-surface-variant)' : 'var(--color-primary)',
                    fontWeight: 700,
                    fontSize: 14,
                    padding: 0,
                  }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                </button>
              </div>
            </FadeInAnimation>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpPage;
